import * as signalR from "@microsoft/signalr";
import Emitter from "@/services/Emitter";
import Http, { getApiBaseUrl } from "@/services/Http";
import ActivityTracker from "@/services/ActivityTracker";
import Alert from "@/services/Alert";

/**
 * WebSocketService manages the SignalR connection for real-time notifications
 * Connects to the backend NotificationHub and routes messages to the global Emitter
 */
class WebSocketService {
    private connection: signalR.HubConnection | null = null;
    private isConnected = false;
    private isConnecting = false;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 3000; // Start with 3 seconds
    private isIntentionallyDisconnected = false;
    private isIdleDisconnect = false;
    private activityUnsubscribers: Function[] = [];

    /**
     * Establishes a SignalR connection to the notification hub
     * @param token JWT token for authentication (passed via query string for WebSocket compatibility)
     */
    public async connect(token?: string): Promise<void> {
        if ((this.connection && this.isConnected) || this.isConnecting) {
            console.log("[WebSocket] Already connected or connecting");
            return;
        }
        this.isConnecting = true;

        // If no token provided, try to get from localStorage
        const authToken = token || localStorage.getItem("token");
        if (!authToken) {
            console.warn("[WebSocket] No authentication token available");
            return;
        }

        try {
            // Build the hub URL with the token in query string
            // SignalR supports passing the token via access_token query parameter
            const baseUrl = getApiBaseUrl().replace("/v1", ""); // Remove /v1 since SignalR hub is at root
            const hubUrl = `${baseUrl}/hubs/notifications?access_token=${encodeURIComponent(authToken)}`;

            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(hubUrl, {
                    transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents,
                    skipNegotiation: false,
                })
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: (retryContext: { previousRetryCount: number; elapsedMilliseconds: number; retryReason: Error }) => {
                        this.reconnectAttempts++;
                        if (this.reconnectAttempts > this.maxReconnectAttempts) {
                            console.error("[WebSocket] Max reconnection attempts reached");
                            return null; // Stop trying
                        }
                        // Exponential backoff: 3s, 6s, 12s, 24s, 30s (max)
                        const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
                        console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
                        return delay;
                    }
                })
                .configureLogging(signalR.LogLevel.Information)
                .build();

            // Wire up message handlers
            this.registerMessageHandlers();

            // Handle connection lifecycle events
            this.connection.onreconnecting((error?: Error) => {
                console.warn("[WebSocket] Connection lost, attempting to reconnect...", error);
                this.isConnected = false;
            });

            this.connection.onreconnected((connectionId?: string) => {
                console.log("[WebSocket] Reconnected with ID:", connectionId);
                this.isConnected = true;
                this.reconnectAttempts = 0;
                Emitter.emit("websocket:connected", connectionId);
            });

            this.connection.onclose((error?: Error) => {
                this.isConnected = false;
                if (this.isIntentionallyDisconnected) {
                    console.log("[WebSocket] Connection closed intentionally");
                } else {
                    console.error("[WebSocket] Connection closed unexpectedly", error);
                    Emitter.emit("websocket:disconnected", error);
                }
            });

            // Start the connection
            await this.connection.start();
            this.isConnected = true;
            this.isIntentionallyDisconnected = false;
            this.isIdleDisconnect = false;
            this.reconnectAttempts = 0;
            console.log("[WebSocket] Connected to SignalR hub");
            Emitter.emit("websocket:connected", this.connection.connectionId);

            // Start activity tracking for idle detection
            this.startActivityTracking();

        } catch (error) {
            console.error("[WebSocket] Failed to connect:", error);
            Emitter.emit("websocket:error", error);
            throw error;
        } finally {
            this.isConnecting = false;
        }
    }

    /**
     * Disconnects from the SignalR hub (explicit logout path).
     * Stops both the connection and activity tracking.
     */
    public async disconnect(): Promise<void> {
        this.isIntentionallyDisconnected = true;

        // Stop activity tracking
        this.stopActivityTracking();

        await this.stopConnection();
    }

    /**
     * Stops only the SignalR connection without touching activity tracking.
     * Used for idle disconnects so activity listeners remain registered
     * and can trigger a reconnect when the user returns.
     */
    private async stopConnection(): Promise<void> {
        if (this.connection) {
            try {
                await this.connection.stop();
                console.log("[WebSocket] Connection stopped");
            } catch (error) {
                console.error("[WebSocket] Error stopping connection:", error);
            } finally {
                this.connection = null;
                this.isConnected = false;
            }
        }
    }

    /**
     * Start tracking user activity for idle detection
     */
    private startActivityTracking(): void {
        // Clean up any existing subscriptions before re-subscribing (e.g. after idle reconnect)
        this.activityUnsubscribers.forEach((unsub) => unsub());
        this.activityUnsubscribers = [];

        ActivityTracker.startTracking();

        // Store unsubscribe functions
        const boundOnActivity = this.onActivity.bind(this);
        const boundOnIdle = this.onIdle.bind(this);

        this.activityUnsubscribers = [
            Emitter.on("user:activity", boundOnActivity),
            Emitter.on("user:idle", boundOnIdle)
        ];
    }

    /**
     * Stop tracking user activity
     */
    private stopActivityTracking(): void {
        ActivityTracker.stopTracking();

        // Unsubscribe all listeners
        this.activityUnsubscribers.forEach((unsub) => unsub());
        this.activityUnsubscribers = [];
    }

    /**
     * Called when user activity is detected
     */
    private onActivity(): void {
        // If we were disconnected due to idle, reconnect immediately
        if (this.isIdleDisconnect && !this.isConnected) {
            console.log("[WebSocket] Activity detected, reconnecting...");
            this.isIdleDisconnect = false;
            this.connect();
        }
    }

    /**
     * Called when user has been idle for 15 minutes
     */
    private onIdle(): void {
        if (this.isConnected) {
            console.log("[WebSocket] Idle timeout reached, disconnecting...");
            this.isIdleDisconnect = true;
            this.isIntentionallyDisconnected = true; // Suppress unexpected-close log in onclose
            this.stopConnection();
            Alert.notify("Disconnected due to inactivity. Will reconnect when you return.", "WebSocket");
        }
    }

    /**
     * Register message handlers that route SignalR messages to the global Emitter
     */
    private registerMessageHandlers(): void {
        if (!this.connection) return;

        // Handle notification refresh events
        this.connection.on("notification:refresh", (payload: any) => {
            console.log("[WebSocket] Received notification:refresh", payload);
            Emitter.emit("notification:refresh", payload);
        });

        // Handle task update events
        this.connection.on("task-update", (payload: any) => {
            console.log("[WebSocket] Received task-update", payload);
            Emitter.emit("task-update", payload);
        });

        // handle notification update events
        this.connection.on("notification-update", (payload: any) => {
            console.log("[WebSocket] Received notification-update", payload);
            Emitter.emit("notification-update", payload);
        });

        // Handle generic update events
        this.connection.on("update", (payload: any) => {
            console.log("[WebSocket] Received update", payload);
            Emitter.emit("websocket:update", payload);
        });

        // Generic handler for any server-sent event
        // Allows the server to send arbitrary event names that will be forwarded to Emitter
        this.connection.on("event", (eventName: string, payload: any) => {
            console.log(`[WebSocket] Received event: ${eventName}`, payload);
            Emitter.emit(eventName, payload);
        });
    }

    /**
     * Join a specific group for targeted notifications (e.g., department, role)
     */
    public async joinGroup(groupName: string): Promise<void> {
        if (!this.connection || !this.isConnected) {
            console.warn("[WebSocket] Cannot join group - not connected");
            return;
        }
        try {
            await this.connection.invoke("JoinGroup", groupName);
            console.log(`[WebSocket] Joined group: ${groupName}`);
        } catch (error) {
            console.error(`[WebSocket] Error joining group ${groupName}:`, error);
        }
    }

    /**
     * Leave a specific group
     */
    public async leaveGroup(groupName: string): Promise<void> {
        if (!this.connection || !this.isConnected) {
            return;
        }
        try {
            await this.connection.invoke("LeaveGroup", groupName);
            console.log(`[WebSocket] Left group: ${groupName}`);
        } catch (error) {
            console.error(`[WebSocket] Error leaving group ${groupName}:`, error);
        }
    }

    /**
     * Check if the WebSocket is currently connected
     */
    public get connected(): boolean {
        return this.isConnected;
    }

    /**
     * Get the current connection state (useful for reading initial state on component mount)
     */
    public getConnected(): boolean {
        return this.isConnected;
    }

    /**
     * Get the current connection ID (if connected)
     */
    public get connectionId(): string | null {
        return this.connection?.connectionId || null;
    }
}

// Export singleton instance
export default new WebSocketService();
